import axios, { AxiosResponse } from 'axios';

// Use the correct backend port
// const API_BASE_URL = 'http://localhost:5000/api';
const API_BASE_URL = window.location.hostname === 'localhost' ? 'https://localhost:7000/api' : 'https://dealden.ca/api';


// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for CORS with credentials and httpOnly cookies
});

// Create separate axios instance for file uploads without Content-Type header
const fileApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Important for httpOnly cookies
  // No Content-Type header - let browser set it automatically
});

// CSRF token cache
let csrfToken: string | null = null;

// Function to get CSRF token
const getCsrfToken = async (): Promise<string | null> => {
  if (csrfToken) {
    return csrfToken;
  }
  
  try {
    const response = await axios.get(`${API_BASE_URL}/auth/csrf-token`, {
      withCredentials: true
    });
    csrfToken = response.data.token;
    return csrfToken;
  } catch (error) {
    console.error('Failed to get CSRF token:', error);
    return null;
  }
};

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    // HttpOnly cookies are automatically sent by the browser
    
    // Add CSRF token for state-changing requests
    if (config.method && ['post', 'put', 'delete', 'patch'].includes(config.method.toLowerCase())) {
      const token = await getCsrfToken();
      if (token) {
        config.headers['X-CSRF-Token'] = token;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Silently handle ad view tracking errors to prevent console spam for visitors
    if (error.config?.url?.includes('/view') && (error.response?.status === 404 || error.response?.status === 401)) {
      // Return a mock successful response for view tracking errors
      return Promise.resolve({
        data: { message: 'View tracking skipped' },
        status: 200,
        statusText: 'OK',
        headers: {},
        config: error.config
      });
    }

    // Only log errors in development mode for non-view-tracking requests
    if (process.env.NODE_ENV === 'development' && !error.config?.url?.includes('/view')) {
      console.group('🔴 API Error');
      console.error('Request URL:', error.config?.url);
      console.error('Status:', error.response?.status);
      console.error('Message:', error.response?.data?.message || error.message);
      console.groupEnd();
    }

    if (error.response?.status === 401) {
      // Only redirect to login if we're not already on auth pages and not making auth-related calls
      const currentPath = window.location.pathname;
      const authPages = ['/login', '/register', '/admin/login', '/', '/forgot-password', '/reset-password'];
      const publicPages = ['/search', '/category', '/ad', '/ads', '/user'];
      const authEndpoints = ['/auth/login', '/auth/verify-otp', '/auth/register', '/auth/me'];
      const publicEndpoints = ['/category', '/banner', '/location'];
      
      const isAuthEndpoint = authEndpoints.some(endpoint => error.config?.url?.includes(endpoint));
      const isPublicEndpoint = publicEndpoints.some(endpoint => error.config?.url?.includes(endpoint));
      const isViewEndpoint = error.config?.url?.includes('/view');
      const isReportEndpoint = error.config?.url?.includes('/adreport');
      const isOnAdminPage = currentPath.startsWith('/admin');
      const isOnPublicPage = authPages.includes(currentPath) || publicPages.some(page => currentPath.startsWith(page));
      
      // On admin pages, 401 for /auth/me is expected (admin may not be a regular user)
      // Don't redirect if we're on public pages, admin pages, making auth/public calls, view tracking, report submission, or during app initialization
      if (!isOnPublicPage && !isOnAdminPage && !isAuthEndpoint && !isPublicEndpoint && !isViewEndpoint && !isReportEndpoint) {
        console.warn('Unauthorized access detected, redirecting to login');
        // Use replace to avoid creating history entries
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Add the same interceptors to fileApi as the main api
fileApi.interceptors.request.use(
  (config) => {
    // HttpOnly cookies are automatically sent by the browser
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

fileApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only log errors in development mode
    if (process.env.NODE_ENV === 'development') {
      console.group('🔴 File API Error');
      console.error('Request URL:', error.config?.url);
      console.error('Status:', error.response?.status);
      console.error('Message:', error.response?.data?.message || error.message);
      console.groupEnd();
    }

    if (error.response?.status === 401) {
      // Only redirect to login if we're not already on auth pages and not making auth-related calls
      const currentPath = window.location.pathname;
      const authPages = ['/login', '/register', '/admin/login', '/', '/forgot-password', '/reset-password'];
      const publicPages = ['/search', '/category', '/ad', '/ads', '/user'];
      const authEndpoints = ['/auth/login', '/auth/verify-otp', '/auth/register', '/auth/me', '/auth/forgot-password', '/auth/reset-password'];
      const isAuthEndpoint = authEndpoints.some(endpoint => error.config?.url?.includes(endpoint));
      const isOnPublicPage = authPages.includes(currentPath) || publicPages.some(page => currentPath.startsWith(page));
      
      // Don't redirect if we're on public pages, making auth calls, or during app initialization
      if (!isOnPublicPage && !isAuthEndpoint) {
        console.warn('Unauthorized file upload detected, redirecting to login');
        // Use replace to avoid creating history entries
        window.location.replace('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Types
export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  isVerified: boolean;
  userType: 'Individual' | 'Business'; // Keep as string for frontend display
  profileImageUrl?: string;
  dateJoined: string;
  city?: string;
  province?: string;
  // Business-specific fields
  businessName?: string;
  businessDescription?: string;
  businessWebsite?: string;
  businessAddress?: string;
  businessHours?: string; // JSON string from backend
  businessGallery?: string; // JSON string from backend (legacy)
  businessSlug?: string;
  isBusinessVerified?: boolean;
  // Business approval status fields
  businessApprovalStatus?: BusinessApprovalStatus;
  lastBusinessApprovedAt?: string;
  businessRejectionReason?: string;
  businessSubmittedAt?: string;
  pendingBusinessChanges?: string;
  // New gallery images from BusinessGalleryImages table
  GalleryImages?: BusinessGalleryImage[];
}

export interface BusinessHours {
  monday: { open: string; close: string; closed: boolean };
  tuesday: { open: string; close: string; closed: boolean };
  wednesday: { open: string; close: string; closed: boolean };
  thursday: { open: string; close: string; closed: boolean };
  friday: { open: string; close: string; closed: boolean };
  saturday: { open: string; close: string; closed: boolean };
  sunday: { open: string; close: string; closed: boolean };
}

export interface UpdateBusinessProfileRequest {
  businessName?: string;
  businessDescription?: string;
  businessWebsite?: string;
  businessAddress?: string;
  businessHours?: BusinessHours;
}

export interface UpdateUserProfileRequest {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  city?: string;
  province?: string;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  iconUrl?: string;
  sortOrder: number;
  isActive: boolean;
  parentCategoryId?: string;
  subCategories: Category[];
}

export interface CategoryAttribute {
  id: string;
  name: string;
  label: string;
  type: 'Text' | 'Number' | 'Boolean' | 'Date' | 'Dropdown' | 'MultiSelect' | 'TextArea' | 'Url' | 'Email';
  defaultValue?: string;
  isRequired: boolean;
  isFilterable: boolean;
  isSearchable: boolean;
  sortOrder: number;
  validationRules?: string;
  options?: string; // JSON string for dropdown options
}

export interface CategoryMetadata {
  id: string;
  name: string;
  displayLayout?: 'default' | 'automotive' | 'electronics' | 'realestate' | 'fashion';
  attributeGroups?: { [groupName: string]: string[] };
  highlightFields?: string[]; // Fields to highlight in selling points
}

export interface Ad {
  id: string;
  title: string;
  slug: string;
  description: string;
  price: number;
  categoryId: string;
  categoryName: string;
  subCategoryId?: string;
  subCategoryName?: string;
  status: 'Active' | 'Sold' | 'Expired' | 'Paused' | 'PendingApproval';
  city: string;
  province: string;
  postalCode: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  userId: string;
  userName: string;
  userIsVerified: boolean;
  userType: 'Individual' | 'Business';
  userPhoneNumber?: string;
  businessSlug?: string;
  businessApprovalStatus?: number | string;
  datePosted: string;
  dateUpdated?: string;
  viewCount: number;
  images: AdImage[];
  videos: AdVideo[];
  attributes: AdAttribute[];
  isFavorited: boolean;
  isFeatured: boolean;
  isUrgent: boolean;
  isHighlighted: boolean;
  isTopAd: boolean;
  isPromoted: boolean;
  isNegotiable?: boolean;
  allowPhoneContact?: boolean;
  allowEmailContact?: boolean;
  allowChatContact?: boolean;
}

export interface AdListItem {
  id: string;
  title: string;
  slug: string;
  price: number;
  categoryName: string;
  subCategoryName?: string;
  status?: 'Active' | 'Sold' | 'Expired' | 'Paused' | 'PendingApproval';
  city: string;
  province: string;
  datePosted: string;
  viewCount: number;
  favoriteCount: number;
  primaryImageUrl?: string;
  isFavorited: boolean;
  isFeatured: boolean;
  isUrgent: boolean;
  isHighlighted: boolean;
  isTopAd: boolean;
  distance?: number;
}

export interface AdImage {
  id: string;
  fileName: string;
  url: string;
  thumbnailUrl?: string;
  sortOrder: number;
  isPrimary: boolean;
}

export interface AdVideo {
  id: string;
  fileName: string;
  url: string;
  thumbnailUrl?: string;
  sortOrder: number;
  durationSeconds?: number;
}

// Response from temporary image upload which may include AI-generated metadata
export interface AdImageTempDto {
  title?: string;
  description?: string;
  thumbnailUrl?: string;
}

export interface AdAttribute {
  id: string;
  name: string;
  label: string;
  type: 'Text' | 'Number' | 'Boolean' | 'Dropdown' | 'Date';
  value: string;
}

export interface CreateAdRequest {
  marketingCode?: string;
  title: string;
  description: string;
  price: number;
  categoryId: string;
  subCategoryId?: string;
  city: string;
  province: string;
  postalCode: string;
  address: string;
  latitude: number;
  longitude: number;
  attributes?: { [key: string]: string };
  promotionPackageId: string;
}

export interface UpdateAdRequest extends Partial<CreateAdRequest> {}

export interface SearchRequest {
  searchTerm?: string;
  categoryId?: string;
  userId?: string;
  excludeUserId?: string;
  excludeAdId?: string;
  minPrice?: number;
  maxPrice?: number;
  city?: string;
  province?: string;
  sortBy?: 'date_new' | 'date_old' | 'price_low' | 'price_high';
  page?: number;
  pageSize?: number;
}

export interface LoginRequest {
  EmailOrPhone: string;
  Password?: string;
}

export interface VerifyOtpRequest {
  EmailOrPhone: string;
  Code: string;
}

export interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  userType: 'Individual' | 'Business';
  businessName?: string;
  businessDescription?: string;
  businessLicense?: string;
  businessWebsite?: string;
  password?: string;
  confirmPassword?: string;
}

export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  userType: 0 | 1; // 0 = Individual, 1 = Business (matching backend enum)
  businessName?: string;
  businessDescription?: string;
  businessLicense?: string;
  businessWebsite?: string;
  password?: string;
}

// Auth API
export const authApi = {
  login: async (data: LoginRequest): Promise<AxiosResponse<{ message: string; user?: User; requiresOtpVerification?: boolean }>> => 
    api.post('/auth/login', data),
  
  verifyOtp: async (data: VerifyOtpRequest): Promise<AxiosResponse<{ message: string; user: User }>> => 
    api.post('/auth/verify-otp', data),
  
  register: async (data: RegisterRequest): Promise<AxiosResponse<{ message: string; user: User }>> => 
    api.post('/auth/register', data),
  
  forgotPassword: async (email: string): Promise<AxiosResponse<{ message: string }>> => 
    api.post('/auth/forgot-password', { email }),
  
  resetPassword: async (token: string, newPassword: string): Promise<AxiosResponse<{ message: string }>> => 
    api.post('/auth/reset-password', { token, newPassword }),
  
  logout: async (): Promise<AxiosResponse<{ message: string }>> => 
    api.post('/auth/logout'),
  
  getCurrentUser: async (): Promise<AxiosResponse<User>> => 
    api.get('/auth/me'),
  
  checkAuth: async (): Promise<AxiosResponse<User>> => 
    api.get('/auth/me'),

  activateEmail: async (data: { token: string }): Promise<AxiosResponse<{ message: string }>> => 
    api.post('/auth/activate-email', data),

  resendActivationEmail: async (data: { email: string }): Promise<AxiosResponse<{ message: string }>> => 
    api.post('/auth/resend-activation', data),
};

// Categories API
export const categoriesApi = {
  getAll: async (): Promise<AxiosResponse<Category[]>> => 
    api.get('/category'),
  
  getById: async (id: string): Promise<AxiosResponse<Category>> => 
    api.get(`/category/${id}`),
  
  getAttributes: async (id: string): Promise<AxiosResponse<CategoryAttribute[]>> => 
    api.get(`/category/${id}/attributes`),
  
  createAttribute: async (categoryId: string, data: any): Promise<AxiosResponse<CategoryAttribute>> => 
    api.post(`/admin/categories/${categoryId}/attributes`, data),
  
  updateAttribute: async (categoryId: string, attributeId: string, data: any): Promise<AxiosResponse<CategoryAttribute>> => 
    api.put(`/admin/categories/${categoryId}/attributes/${attributeId}`, data),
  
  deleteAttribute: async (categoryId: string, attributeId: string): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/admin/categories/${categoryId}/attributes/${attributeId}`),
  
  getMetadata: async (id: string): Promise<AxiosResponse<CategoryMetadata>> => 
    api.get(`/category/${id}/metadata`),
};

// Ads API
export const adsApi = {
  search: async (params: SearchRequest): Promise<AxiosResponse<{ ads: AdListItem[]; totalCount: number }>> => 
    api.get('/ad/search', { params }),
  
  getById: async (id: string): Promise<AxiosResponse<Ad>> => 
    api.get(`/ad/${id}`),
  
  getBySlug: async (slug: string): Promise<AxiosResponse<Ad>> => 
    api.get(`/ad/slug/${slug}`),
  
  create: async (data: CreateAdRequest): Promise<AxiosResponse<Ad>> => 
    api.post('/ad', data),
  
  update: async (id: string, data: UpdateAdRequest): Promise<AxiosResponse<Ad>> => 
    api.put(`/ad/${id}`, data),
  
  delete: async (id: string): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/ad/${id}`),
  
  getMyAds: async (): Promise<AxiosResponse<AdListItem[]>> => 
    api.get('/ad/my-ads'),
  
  getUserAds: async (userId: string): Promise<AxiosResponse<AdListItem[]>> => 
    api.get(`/ad/user/${userId}`),
  
  toggleFavorite: async (id: string): Promise<AxiosResponse<{ message: string; isFavorited: boolean }>> => 
    api.post(`/ad/${id}/favorite`),
  
  getFavorites: async (page: number = 1, pageSize: number = 10): Promise<AxiosResponse<PagedResult<AdListItem>>> => 
    api.get(`/ad/favorites?page=${page}&pageSize=${pageSize}`),
  
  incrementView: async (id: string): Promise<AxiosResponse<{ message: string }>> => {
    // Return a mock response without making the actual API call
    // This prevents any console errors for visitors
    return Promise.resolve({
      data: { message: 'View tracking handled silently' },
      status: 200,
      statusText: 'OK',
      headers: {},
      config: {
        url: `/ad/${id}/view`,
        method: 'post'
      }
    } as AxiosResponse<{ message: string }>);
  },

  boostAdRanking: async (id: string): Promise<AxiosResponse<{ message: string; remainingBoosts: number }>> => 
    api.post(`/ad/${id}/boost-ranking`),

  bumpUpAd: async (id: string): Promise<AxiosResponse<{ message: string; remainingBumpUps: number }>> => 
    api.post(`/ad/${id}/bump-up`),

  markAsSold: async (id: string): Promise<AxiosResponse<{ message: string }>> => 
    api.post(`/ad/${id}/mark-sold`),

  applyTopAd: async (id: string): Promise<AxiosResponse<{ message: string }>> => 
    api.post(`/ad/${id}/apply-top-ad`),

  applyHighlight: async (id: string): Promise<AxiosResponse<{ message: string }>> => 
    api.post(`/ad/${id}/apply-highlight`),

  applyUrgentTag: async (id: string): Promise<AxiosResponse<{ message: string }>> => 
    api.post(`/ad/${id}/apply-urgent-tag`),
};

// File Upload API
export const fileUploadApi = {
  uploadImage: async (adId: string, file: File, isPrimary: boolean = false): Promise<AxiosResponse<AdImage>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('isPrimary', isPrimary.toString());
    
    // Use dedicated fileApi instance without Content-Type header
    return fileApi.post(`/fileupload/ad/${adId}/image`, formData);
  },
  
  uploadVideo: async (adId: string, file: File): Promise<AxiosResponse<AdVideo>> => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Use dedicated fileApi instance without Content-Type header
    return fileApi.post(`/fileupload/ad/${adId}/video`, formData);
  },
  
  deleteImage: async (imageId: string): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/fileupload/image/${imageId}`),
  
  deleteVideo: async (videoId: string): Promise<AxiosResponse<{ message: string }>> => 
    api.delete(`/fileupload/video/${videoId}`),
  
  setPrimaryImage: async (imageId: string): Promise<AxiosResponse<{ message: string }>> => 
    api.put(`/fileupload/image/${imageId}/primary`),
  
  getAdImages: async (adId: string): Promise<AxiosResponse<AdImage[]>> => 
    api.get(`/fileupload/ad/${adId}/images`),
  
  getAdVideos: async (adId: string): Promise<AxiosResponse<AdVideo[]>> => 
    api.get(`/fileupload/ad/${adId}/videos`),
  
  /**
   * Upload image to temporary endpoint to get AI-generated title/description (does not require an adId)
   * Backend route: POST /fileupload/ad/imagetemp
   */
  uploadImageTemp: async (file: File): Promise<AxiosResponse<AdImageTempDto>> => {
    const formData = new FormData();
    formData.append('file', file);
    return fileApi.post(`/fileupload/ad/imagetemp`, formData);
  },
};

// Location API
export const locationApi = {
  getProvinces: async (): Promise<AxiosResponse<{ id: string; name: string; code: string }[]>> => 
    api.get('/location/provinces'),
  
  getCities: async (province?: string): Promise<AxiosResponse<{ id: string; name: string; province: string; provinceCode: string; population?: number }[]>> => 
    api.get('/location/cities', { params: province ? { province } : {} }),
  
  getPopularLocations: async (): Promise<AxiosResponse<{ city: string; province: string; count: number }[]>> => 
    api.get('/location/popular-locations'),

  seedLocations: async (): Promise<AxiosResponse<{ message: string }>> => 
    api.post('/location/seed'),
};

// Payment API
export interface CreatePaymentSessionRequest {
  packageId: string;
  packageName: string;
  amount: number;
}

export interface PaymentDto {
  id: string;
  amount: number;
  currency: string;
  status: string;
  method: string;
  transactionId?: string;
  paymentProcessorId?: string;
  createdDate: string;
  completedDate?: string;
  failureReason?: string;
  userId: string;
  promotionPackageId?: string;
}

export interface UserPlanDto {
  id: string;
  promotionPackageId: string;
  name: string;
  description: string;
  price: number;
  durationDays: number;
  purchaseDate: string;
  expirationDate: string;
  isActive: boolean;
  isExpired: boolean;
  remainingDays: number;
  maxAdsCount: number;
  usedAdsCount: number;
  remainingAdsCount: number;
  hasRemainingAds: boolean;
  features: string;
  iconEmoji: string;
  colorClass: string;
  rankingBoost: number;
  usedRankingBoosts: number;
  remainingRankingBoosts: number;
  
  // Count-based promotional features
  usedTopAdCount: number;
  usedHighlightCount: number;
  usedUrgentTagCount: number;
  usedBumpUpCount: number;
  usedHomepageGalleryCount: number;
  remainingTopAdCount: number;
  remainingHighlightCount: number;
  remainingUrgentTagCount: number;
  remainingBumpUpCount: number;
  remainingHomepageGalleryCount: number;
}

export interface PagedResult<T> {
  data: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export const paymentApi = {
  createSession: async (data: CreatePaymentSessionRequest): Promise<AxiosResponse<{ message: string; checkoutUrl: string }>> =>
    api.post('/payment/create-session', data),
    
  verifyPayment: async (sessionId: string): Promise<AxiosResponse<{ status: string; message: string; payment?: PaymentDto }>> =>
    api.post('/payment/verify-payment', { sessionId }),
    
  getPayment: async (id: string): Promise<AxiosResponse<PaymentDto>> =>
    api.get(`/payment/${id}`),
    
  getUserPayments: async (page: number = 1, pageSize: number = 10): Promise<AxiosResponse<PagedResult<PaymentDto>>> =>
    api.get(`/payment/user-payments?page=${page}&pageSize=${pageSize}`),
    
  getUserPlans: async (): Promise<AxiosResponse<UserPlanDto[]>> =>
    api.get('/payment/user-plans'),

  activateFreePlan: async (packageId: string): Promise<AxiosResponse<{ message: string; planId: string }>> =>
    api.post('/payment/activate-free-plan', { packageId }),
};

// User Analytics API
export const analyticsApi = {
  getUserStats: async () => {
    console.log('Calling getUserStats API...');
    try {
      const response = await api.get('/useranalytics/user-stats');
      
      return response;
    } catch (error) {
  
      throw error;
    }
  },

  getAdViews: async (days: number = 30) => {
    console.log('Calling getAdViews API with days:', days);
    try {
      const response = await api.get(`/useranalytics/ad-views?days=${days}`);
   
      return response;
    } catch (error) {
    
      throw error;
    }
  },

  getCategoryStats: async () => {
    console.log('Calling getCategoryStats API...');
    try {
      const response = await api.get('/useranalytics/category-stats');
    
      return response;
    } catch (error) {
      
      throw error;
    }
  },

  getMonthlyStats: async () => {  
    try {
      const response = await api.get('/useranalytics/monthly-stats');
      console.log('getMonthlyStats response:', response);
      return response;
    } catch (error) {     
      throw error;
    }
  }
};

// Promotion Package interfaces
export interface PromotionPackage {
  id: string;
  name: string;
  description: string;
  price: number;
  durationDays: number;
  rankingBoost: number;
  includesTopAd: boolean;
  includesHighlight: boolean;
  includesUrgentTag: boolean;
  includesBumpUp: boolean;
  includesHomepageGallery: boolean;
  isActive: boolean;
  features: string[];
  iconEmoji: string;
  colorClass: string;
  isPopular: boolean;
}

export interface Banner {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  linkUrl?: string;
  position: BannerPosition;
  isActive: boolean;
  sortOrder: number;
  startDate?: string;
  endDate?: string;
  createdDate: string;
}

export enum BannerPosition {
  SearchDropdown = 0,
  HomePage_Hero = 1,
  HomePage_Sidebar = 2,
  SearchPage_Top = 3,
  SearchPage_Sidebar = 4,
  AdDetails_Top = 5,
  AdDetails_Sidebar = 6,
  UserProfile_Top = 7,
  Footer = 8
}

export interface SiteSetting {
  id: number;
  frontendUrl: string;
  backendUrl: string;
  apiBaseUrl?: string;
  cdnUrl?: string;
  siteTitle: string;
  siteDescription: string;
  metaKeywords?: string;
  siteLogoUrl?: string;
  faviconUrl?: string;
  openGraphImageUrl?: string;
  siteAuthor: string;
  defaultLanguage: string;
  businessName: string;
  contactEmail: string;
  supportEmail?: string;
  contactPhone?: string;
  businessAddress?: string;
  copyrightText: string;
  facebookUrl?: string;
  twitterUrl?: string;
  instagramUrl?: string;
  telegramUrl?: string;
  linkedInUrl?: string;
  maintenanceMode: boolean;
  siteActive: boolean;
  maxFileUploadSizeMB: number;
  allowedFileTypes: string;
  defaultCurrency: string;
  timezone: string;
  googleAnalyticsId?: string;
  googleTagManagerId?: string;
  facebookPixelId?: string;
  termsOfServiceUrl?: string;
  privacyPolicyUrl?: string;
  cookiePolicyUrl?: string;
  enableRegistration: boolean;
  enableGuestPosting: boolean;
  enableMessaging: boolean;
  enableReviews: boolean;
  enableOtpOnlyLogin: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PublicSiteSetting {
  siteTitle: string;
  siteDescription: string;
  metaKeywords?: string;
  siteLogoUrl?: string;
  faviconUrl?: string;
  openGraphImageUrl?: string;
  siteAuthor: string;
  defaultLanguage: string;
  businessName: string;
  contactEmail: string;
  contactPhone?: string;
  copyrightText: string;
  facebookUrl?: string;
  twitterUrl?: string;
  instagramUrl?: string;
  telegramUrl?: string;
  linkedInUrl?: string;
  maintenanceMode: boolean;
  enableRegistration: boolean;
  enableGuestPosting: boolean;
  enableMessaging: boolean;
  enableReviews: boolean;
  enableOtpOnlyLogin: boolean;
  defaultCurrency: string;
  maxFileUploadSizeMB: number;
  allowedFileTypes: string;
}

export interface UpdateSiteSettingRequest {
  frontendUrl: string;
  backendUrl: string;
  apiBaseUrl?: string;
  cdnUrl?: string;
  siteTitle: string;
  siteDescription: string;
  metaKeywords?: string;
  siteLogoUrl?: string;
  faviconUrl?: string;
  openGraphImageUrl?: string;
  siteAuthor: string;
  defaultLanguage: string;
  businessName: string;
  contactEmail: string;
  supportEmail?: string;
  contactPhone?: string;
  businessAddress?: string;
  copyrightText: string;
  facebookUrl?: string;
  twitterUrl?: string;
  instagramUrl?: string;
  telegramUrl?: string;
  linkedInUrl?: string;
  maintenanceMode: boolean;
  siteActive: boolean;
  maxFileUploadSizeMB: number;
  allowedFileTypes: string;
  defaultCurrency: string;
  timezone: string;
  googleAnalyticsId?: string;
  googleTagManagerId?: string;
  facebookPixelId?: string;
  termsOfServiceUrl?: string;
  privacyPolicyUrl?: string;
  cookiePolicyUrl?: string;
  enableRegistration: boolean;
  enableGuestPosting: boolean;
  enableMessaging: boolean;
  enableReviews: boolean;
  enableOtpOnlyLogin: boolean;
}

// Business Moderation Types
export interface BusinessProfile {
  id: string;
  businessName?: string;
  businessDescription?: string;
  businessWebsite?: string;
  businessHours?: string;
  businessSlug?: string;
  isBusinessVerified: boolean;
  businessApprovalStatus: BusinessApprovalStatus;
  lastBusinessApprovedAt?: string;
  businessRejectionReason?: string;
  businessSubmittedAt?: string;
  galleryImages: BusinessGalleryImage[];
}

export interface BusinessGalleryImage {
  id: string;
  imageUrl: string;
  imageDescription?: string;
  uploadedAt: string;
  moderationStatus: ImageModerationStatus;
  moderatedAt?: string;
  rejectionReason?: string;
  displayOrder: number;
}

export interface UploadBusinessGalleryImageRequest {
  imageUrl: string;
  imageDescription?: string;
  displayOrder?: number;
}

export interface BusinessModerationItem {
  userId: string;
  businessName: string;
  currentStatus: BusinessApprovalStatus;
  submittedAt?: string;
  pendingChanges?: string;
  pendingImages: BusinessGalleryImage[];
}

export interface ApproveBusinessProfileRequest {
  userId: string;
  isApproved: boolean;
  rejectionReason?: string;
  moderationNotes?: string;
}

export interface ModerateGalleryImageRequest {
  imageId: string;
  status: ImageModerationStatus;
  rejectionReason?: string;
  moderationNotes?: string;
}

export enum BusinessApprovalStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2
}

export enum ImageModerationStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
  UnderReview = 3
}

// Promotion Packages API
export const promotionPackagesApi = {
  getActive: (): Promise<AxiosResponse<PromotionPackage[]>> =>
    api.get('/promotionpackages'),

  getById: (id: string): Promise<AxiosResponse<PromotionPackage>> =>
    api.get(`/promotionpackages/${id}`),
};

// Banner API
export const bannerApi = {
  getByPosition: (position: BannerPosition): Promise<AxiosResponse<Banner[]>> =>
    api.get(`/banner/position/${position}`),

  getAll: (): Promise<AxiosResponse<Banner[]>> =>
    api.get('/banner'),

  getById: (id: string): Promise<AxiosResponse<Banner>> =>
    api.get(`/banner/${id}`),
};

// Site Settings API
export const siteSettingsApi = {
  // Public endpoint - no authentication required
  getPublic: (): Promise<AxiosResponse<PublicSiteSetting>> =>
    api.get('/sitesetting/public'),

  // Admin endpoints - require authentication
  get: (): Promise<AxiosResponse<SiteSetting>> =>
    api.get('/admin/adminsitesettings'),

  update: (data: UpdateSiteSettingRequest): Promise<AxiosResponse<SiteSetting>> =>
    api.put('/admin/adminsitesettings', data),

  initialize: (): Promise<AxiosResponse<SiteSetting>> =>
    api.post('/admin/adminsitesettings/initialize'),
};

// User Profile API
export const userApi = {
  getById: (id: string): Promise<AxiosResponse<{ User: User; GalleryImages: BusinessGalleryImage[] }>> =>
    api.get(`/user/${id}`),

  getBusinessBySlugAndId: (slug: string, id: string): Promise<AxiosResponse<{ user?: User; redirectSlug?: string }>> =>
    api.get(`/user/business/${slug}/${id}`),

  getBusinessBySlug: (slug: string): Promise<AxiosResponse<{ User: User; GalleryImages: BusinessGalleryImage[] }>> =>
    api.get(`/user/business/${slug}`),

  updateProfile: (data: UpdateUserProfileRequest): Promise<AxiosResponse<{ message: string; user: User }>> =>
    api.put('/user/profile', data),

  updateBusinessProfile: (data: UpdateBusinessProfileRequest): Promise<AxiosResponse<{ message: string; user: User }>> => {
    // Convert BusinessHours object to JSON string for backend
    const requestData = {
      ...data,
      businessHours: data.businessHours ? JSON.stringify(data.businessHours) : undefined
    };
    return api.put('/user/business-profile', requestData);
  },
};

// Business Profile API (deprecated - use userApi instead)
export const businessApi = {
  updateProfile: (data: UpdateBusinessProfileRequest): Promise<AxiosResponse<User>> =>
    api.put('/user/business-profile', data),

  uploadGalleryImage: (file: File): Promise<AxiosResponse<{ imageUrl: string }>> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/user/business-gallery', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  deleteGalleryImage: (imageUrl: string): Promise<AxiosResponse<void>> =>
    api.delete('/user/business-gallery', { data: { imageUrl } }),
};

// Business Moderation API
export const businessModerationApi = {
  // Business Profile Management
  submitProfile: (data: UpdateBusinessProfileRequest): Promise<AxiosResponse<BusinessProfile>> => {
    // Convert BusinessHours object to JSON string for backend
    const requestData = {
      ...data,
      businessHours: data.businessHours ? JSON.stringify(data.businessHours) : undefined
    };
    return api.post('/businessmoderation/submit-profile', requestData);
  },

  resubmitProfile: (): Promise<AxiosResponse<BusinessProfile>> =>
    api.post('/user/resubmit-business-profile'),

  getPendingProfiles: (): Promise<AxiosResponse<BusinessModerationItem[]>> =>
    api.get('/businessmoderation/pending-profiles'),

  approveProfile: (data: ApproveBusinessProfileRequest): Promise<AxiosResponse<BusinessProfile>> =>
    api.post('/businessmoderation/approve-profile', data),

  // Gallery Image Management
  uploadGalleryImage: (data: UploadBusinessGalleryImageRequest): Promise<AxiosResponse<BusinessGalleryImage>> =>
    api.post('/businessmoderation/upload-gallery-image', data),

  getPendingImages: (): Promise<AxiosResponse<BusinessGalleryImage[]>> =>
    api.get('/businessmoderation/pending-images'),

  moderateImage: (data: ModerateGalleryImageRequest): Promise<AxiosResponse<BusinessGalleryImage>> =>
    api.post('/businessmoderation/moderate-image', data),

  getApprovedImages: (businessUserId: string): Promise<AxiosResponse<BusinessGalleryImage[]>> =>
    api.get(`/businessmoderation/approved-images/${businessUserId}`),

  deleteGalleryImage: (imageId: string): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/businessmoderation/gallery-image/${imageId}`),
};

// Email Template Types
export enum EmailTemplateType {
  Registration = 1,
  EmailVerification = 2,
  PasswordReset = 3,
  Welcome = 4,
  Newsletter = 5,
  AdApproved = 6,
  AdRejected = 7,
  AdExpiring = 8,
  BusinessApproved = 9,
  BusinessRejected = 10,
  PaymentConfirmation = 11,
  PromotionActivated = 12,
  SystemNotification = 13,
  Advertisement = 14,
  ContactForm = 15
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  type: EmailTemplateType;
  typeName: string;
  isActive: boolean;
  isDefault: boolean;
  description?: string;
  availableVariables?: string[];
  createdAt: string;
  updatedAt: string;
  createdByAdminName?: string;
}

export interface CreateEmailTemplateRequest {
  name: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  type: EmailTemplateType;
  isActive: boolean;
  isDefault: boolean;
  description?: string;
}

export interface UpdateEmailTemplateRequest {
  name: string;
  subject: string;
  htmlBody: string;
  textBody?: string;
  isActive: boolean;
  description?: string;
}

export interface EmailTemplateStats {
  totalTemplates: number;
  activeTemplates: number;
  inactiveTemplates: number;
  templatesByType: Record<EmailTemplateType, number>;
  hasDefaultTemplate: Record<EmailTemplateType, boolean>;
}

export interface EmailTemplatePreview {
  subject: string;
  htmlBody: string;
  textBody?: string;
}

// Email Template API
export const emailTemplateApi = {
  getAll: (): Promise<AxiosResponse<EmailTemplate[]>> =>
    api.get('/admin/adminemailtemplate'),

  getByType: (type: EmailTemplateType): Promise<AxiosResponse<EmailTemplate[]>> =>
    api.get(`/admin/adminemailtemplate/by-type/${type}`),

  getById: (id: string): Promise<AxiosResponse<EmailTemplate>> =>
    api.get(`/admin/adminemailtemplate/${id}`),

  create: (data: CreateEmailTemplateRequest): Promise<AxiosResponse<EmailTemplate>> =>
    api.post('/admin/adminemailtemplate', data),

  update: (id: string, data: UpdateEmailTemplateRequest): Promise<AxiosResponse<EmailTemplate>> =>
    api.put(`/admin/adminemailtemplate/${id}`, data),

  delete: (id: string): Promise<AxiosResponse<void>> =>
    api.delete(`/admin/adminemailtemplate/${id}`),

  setAsDefault: (id: string): Promise<AxiosResponse<void>> =>
    api.put(`/admin/adminemailtemplate/${id}/set-default`),

  toggleActive: (id: string): Promise<AxiosResponse<void>> =>
    api.put(`/admin/adminemailtemplate/${id}/toggle-active`),

  getStats: (): Promise<AxiosResponse<EmailTemplateStats>> =>
    api.get('/admin/adminemailtemplate/stats'),

  preview: (id: string, variables: Record<string, string>): Promise<AxiosResponse<EmailTemplatePreview>> =>
    api.post(`/admin/adminemailtemplate/${id}/preview`, variables),
};

// Contact API
export interface CreateContactMessageDto {
  name: string;
  email: string;
  category?: string;
  subject: string;
  message: string;
  honeypot?: string;
}

export interface ContactMessageDto {
  id: string;
  name: string;
  email: string;
  category?: string;
  subject: string;
  message: string;
  ipAddress?: string;
  status: number;
  createdAt: string;
  readAt?: string;
  readByAdminId?: string;
  adminNotes?: string;
}

export const contactApi = {
  submit: (data: CreateContactMessageDto): Promise<AxiosResponse<{ message: string; messageId: string }>> =>
    api.post('/contact', data),
};

export default api; 